import { DOCUMENT, Location } from '@angular/common';
import { Injectable, inject } from '@angular/core';

import { Cinema, Movie, PerformanceGroup } from '@usheru-hff/shared/domain';

import { cleanDeep, toSameAs, toTextArray, toTextOrArray } from './schema.utils';

export type JsonLdSchema = Record<string, any>;

@Injectable({ providedIn: 'root' })
export class StructuredDataService {
  private readonly location = inject(Location);
  private readonly document = inject(DOCUMENT);

  /**
   * A stable @id is the key to:
   * - linking the Movie script and the Events script
   * - avoiding nested Movie objects inside the Events graph (which Google validates separately)
   */
  private getMovieSchemaId(movie: Movie, canonicalUrl?: string): string {
    // Prefer canonical URL because it’s a real IRI Google can understand well.
    // Fallback keeps it deterministic.
    const base = canonicalUrl && canonicalUrl.trim() ? canonicalUrl.trim() : `urn:movie:${movie.id}`;
    return `${base}#movie`;
  }

  private getEventSchemaId(movie: Movie, group: PerformanceGroup, startDateIso: string, canonicalUrl?: string): string {
    const base = canonicalUrl && canonicalUrl.trim() ? canonicalUrl.trim() : 'urn:events';
    const cinemaId = (group?.cinema as any)?.id ?? 'cinema';
    const movieId = movie?.id ?? 'movie';
    // keep it deterministic & unique
    return `${base}#event-${movieId}-${cinemaId}-${startDateIso}`;
  }

  buildJsonLdMovieFromEntity(movie: Movie, canonicalUrl?: string): JsonLdSchema {
    const keywordsFromTags = (): string | undefined => {
      const tags = toTextArray(movie, movie.tags);
      return tags?.length ? tags.join(', ') : undefined;
    };

    // NOTE: you explicitly asked: don't replace genres: getMovieGenres(data as Movie)
    // So we keep your approach based on movie.genres as it exists in the entity.
    const genres = toTextArray(movie, movie.genres);

    const inLanguage =
      typeof movie.language === 'string' && movie.language.trim()
        ? movie.language.trim()
        : toTextOrArray(movie, movie.languages);

    const countryOfOrigin =
      typeof movie.country === 'string' && movie.country.trim()
        ? movie.country.trim()
        : toTextOrArray(movie, movie.customCountries ?? movie.countries);

    const images = Array.from(
      new Set(
        [movie.fullpageImageUrl, movie.tilePhotoUrl, movie.portraitPhotoUrl, movie.logoUrl]
          .map(v => (typeof v === 'string' ? v.trim() : ''))
          .filter(Boolean)
      )
    );

    const contentRating =
      (movie.locCertification as any)?.rating ??
      (movie.locCertification as any)?.name ??
      movie.locAdvisory ??
      movie.certification ??
      movie.advisory;

    const movieSchemaId = this.getMovieSchemaId(movie, canonicalUrl);

    const schema: JsonLdSchema = {
      '@context': 'https://schema.org',
      '@type': 'Movie',
      '@id': movieSchemaId,

      name: movie.title ?? movie.trTitle,
      description: movie.description,
      url: canonicalUrl,
      identifier: movie.id,

      datePublished: this.toIsoDate(movie.releaseDate),
      duration: this.toIsoDuration(movie.runtime),

      genre: genres,
      keywords: keywordsFromTags(),
      contentRating: typeof contentRating === 'string' ? contentRating.trim() : undefined,

      image: images.length ? (images.length === 1 ? images[0] : images) : undefined,

      inLanguage,
      countryOfOrigin,

      sameAs: toSameAs(movie)
    };

    return cleanDeep(schema);
  }

  /** Returns JSON only (no script tag) */
  buildJsonLdString(schema: JsonLdSchema): string {
    return JSON.stringify(schema);
  }

  /** Returns <script type="application/ld+json">...</script> */
  buildJsonLdScriptTag(schema: JsonLdSchema, id?: string): string {
    const json = this.buildJsonLdString(schema);
    const idAttr = id ? ` id="${this.escapeHtmlAttr(id)}"` : '';
    const safeJson = json.replace(/</g, '\\u003c').replace(/>/g, '\\u003e');
    return `<script type="application/ld+json"${idAttr}>${safeJson}</script>`;
  }

  buildJsonLdEventsFromPerformanceGroups(
    movie: Movie,
    performanceGroups: PerformanceGroup[],
    canonicalUrl?: string
  ): JsonLdSchema | undefined {
    const events = performanceGroups.flatMap(group =>
      (group.parsedLocalTimes || [])
        .map(date => this.buildScreeningEventSchema(movie, group, date, canonicalUrl))
        .filter((event): event is JsonLdSchema => !!event)
    );

    if (!events.length) return undefined;

    return cleanDeep({
      '@context': 'https://schema.org',
      '@graph': events
    });
  }

  setPerformanceEventsJsonLd(
    performanceGroups: PerformanceGroup[],
    movie: Movie,
    canonicalUrl?: string,
    id = 'movie-events-jsonld'
  ): void {
    const schema = this.buildJsonLdEventsFromPerformanceGroups(movie, performanceGroups, canonicalUrl);
    if (!schema) {
      this.removeJsonLdScript(id);
      return;
    }

    const json = this.buildJsonLdString(schema).replace(/</g, '\\u003c').replace(/>/g, '\\u003e');
    const existing = this.document.getElementById(id);
    const script = existing ?? this.document.createElement('script');
    script.setAttribute('type', 'application/ld+json');
    script.setAttribute('id', id);
    script.textContent = json;
    if (!existing) this.document.head.appendChild(script);
  }

  /** Inject / update JSON-LD in <head> */
  setMovieJsonLd(movie?: Movie, canonicalUrl?: string, id = 'movie-jsonld'): void {
    const existing = this.document.getElementById(id);

    if (!movie) {
      existing?.remove();
      return;
    }

    const schema = this.buildJsonLdMovieFromEntity(movie, canonicalUrl);
    const json = this.buildJsonLdString(schema).replace(/</g, '\\u003c').replace(/>/g, '\\u003e');

    const script = existing ?? this.document.createElement('script');
    script.setAttribute('type', 'application/ld+json');
    script.setAttribute('id', id);
    script.textContent = json;

    if (!existing) this.document.head.appendChild(script);
  }

  removeJsonLdScript(id = 'movie-jsonld'): void {
    this.document.getElementById(id)?.remove();
  }

  // --- helpers ---
  private buildScreeningEventSchema(
    movie: Movie,
    group: PerformanceGroup,
    date: Date,
    canonicalUrl?: string
  ): JsonLdSchema | undefined {
    if (!date || Number.isNaN(date.getTime())) return undefined;

    const cinema = group.cinema;

    const startDateIso = date.toISOString();

    // REQUIRED for Event rich results: name, startDate, location (Place with name+address). :contentReference[oaicite:1]{index=1}
    const movieName = (movie?.title ?? movie?.trTitle)?.trim();
    const cinemaName = (cinema?.name ?? '')?.trim();

    // Address: Google wants a detailed street address (PostalAddress). :contentReference[oaicite:2]{index=2}
    const streetAddress = (cinema?.address ?? cinema?.place?.address ?? '')?.trim();
    const addressLocality = (cinema?.city ?? cinema?.place?.city?.name ?? '')?.trim();
    const addressRegion = (cinema?.region ?? cinema?.place?.region ?? '')?.trim();
    const addressCountry = (this.getCinemaCountryName(cinema) ?? '')?.trim();

    // Filter out events that will fail required checks (prevents "Unnamed item" spam)
    if (!movieName) return undefined;
    if (!cinemaName) return undefined;
    if (!streetAddress && !addressLocality && !addressCountry) return undefined;

    const movieSchemaId = movie ? this.getMovieSchemaId(movie, canonicalUrl) : undefined;

    // Optional but recommended by Google: image :contentReference[oaicite:3]{index=3}
    const movieImage =
      movie?.fullpageImageUrl || movie?.tilePhotoUrl || movie?.portraitPhotoUrl || movie?.logoUrl || undefined;

    const eventId = this.getEventSchemaId(movie, group, startDateIso, canonicalUrl);

    const address = cleanDeep({
      '@type': 'PostalAddress',
      streetAddress: streetAddress || undefined,
      addressLocality: addressLocality || undefined,
      addressRegion: addressRegion || undefined,
      addressCountry: addressCountry || undefined
    });

    const location = cleanDeep({
      '@type': 'Place',
      name: cinemaName,
      address,
      geo:
        cinema?.latitude && cinema?.longitude
          ? {
              '@type': 'GeoCoordinates',
              latitude: cinema.latitude,
              longitude: cinema.longitude
            }
          : undefined,
      url: cinema?.website
    });

    // IMPORTANT CHANGE:
    // - Do NOT embed a Movie object here (Google validates those as separate Movie items).
    // - Reference the Movie script via @id instead.
    const workPresented = movieSchemaId ? { '@id': movieSchemaId } : undefined;

    return cleanDeep({
      '@type': 'Event',
      '@id': eventId,

      name: `Screening: ${movieName}`, // do not put location name in the event name :contentReference[oaicite:4]{index=4}
      startDate: startDateIso,

      eventStatus: 'https://schema.org/EventScheduled',
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',

      url: canonicalUrl,

      location,
      image: movieImage,

      workPresented,

      // keep references at event level if you want (optional)
      sameAs: movie ? toSameAs(movie) : undefined
    });
  }

  private toIsoDate(ddMmYyyy?: string): string | undefined {
    if (!ddMmYyyy) return undefined;
    const m = ddMmYyyy.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (!m) return undefined;
    const [, dd, mm, yyyy] = m;
    return `${yyyy}-${mm}-${dd}`;
  }

  private toIsoDuration(minutes?: number): string | undefined {
    if (minutes == null) return undefined;
    if (!Number.isFinite(minutes) || minutes <= 0) return undefined;
    return `PT${Math.round(minutes)}M`;
  }

  private escapeHtmlAttr(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  private getCinemaCountryName(cinema?: Cinema): string | undefined {
    if (!cinema) return undefined;
    if (typeof cinema.country === 'string' && cinema.country.trim()) return cinema.country.trim();
    return undefined;
  }
}
